// Board entity types — canonical definitions live in src/types/board.ts.
// Re-exported here for backward compatibility.
export {
  cellKey,
  type CellStack,
  type GridPosition,
  type MegaBlock,
  type MegaBlockDef,
  type LockedDoor,
  type LockedDoorDef,
  type BlockadeWall,
  type BlockadeWallDef,
  type BarricadeDef,
  type Barricade,
} from '../types/board';
import type { CellStack, GridPosition, MegaBlock, MegaBlockDef, LockedDoor, LockedDoorDef, BlockadeWall, BlockadeWallDef, BarricadeDef, Barricade } from '../types/board';
import { cellKey } from '../types/board';

export interface BoardConfig {
  grid: CellStack[][];
  snakeCells?: Set<number>;
  megaBlockDefs?: MegaBlockDef[];
  lockedDoorDefs?: LockedDoorDef[];
  blockadeWallDefs?: BlockadeWallDef[];
  barricadeDefs?: BarricadeDef[];
}

/**
 * Generic overlay for board entities that occupy rectangular cell regions.
 * Encapsulates the shared pattern: entity map, cell map, add/get/remove methods.
 */
class BoardOverlay<T extends { id: number }> {
  readonly entities = new Map<number, T>();
  readonly cellMap = new Map<number, number>();
  private nextId = 1;

  allocateId(): number {
    return this.nextId++;
  }

  /** Register an entity and map its cells to its ID. */
  add(entity: T, cells: number[]): void {
    this.entities.set(entity.id, entity);
    for (const key of cells) {
      this.cellMap.set(key, entity.id);
    }
  }

  /** Get entity by ID. */
  get(id: number): T | undefined {
    return this.entities.get(id);
  }

  /** Get the entity ID at a cell, or undefined. */
  getCellId(row: number, col: number): number | undefined {
    return this.cellMap.get(cellKey(row, col));
  }

  /** Get the entity covering a cell, or undefined. */
  getAt(row: number, col: number): T | undefined {
    const id = this.cellMap.get(cellKey(row, col));
    if (id === undefined) return undefined;
    return this.entities.get(id);
  }

  /** Check if a cell is covered by an entity. */
  isCell(row: number, col: number): boolean {
    return this.cellMap.has(cellKey(row, col));
  }

  /** Get all active entities as a readonly map. */
  getAll(): ReadonlyMap<number, T> {
    return this.entities;
  }

  /** Remove an entity and all its cell mappings. */
  remove(id: number): void {
    this.entities.delete(id);
    for (const [key, eid] of this.cellMap) {
      if (eid === id) this.cellMap.delete(key);
    }
  }

  /** Remove only the cell mappings for an entity (keep entity in registry). */
  removeCells(id: number): void {
    for (const [key, eid] of this.cellMap) {
      if (eid === id) this.cellMap.delete(key);
    }
  }
}

export class BoardSystem {
  private grid: CellStack[][];
  /** Set of numeric cell keys marking cells that are part of a snake block. */
  private snakeCells: Set<number> = new Set();
  private megaBlocks = new BoardOverlay<MegaBlock>();
  private lockedDoors = new BoardOverlay<LockedDoor>();
  private barricades = new BoardOverlay<Barricade>();
  /** Registry of blockade walls by ID (no cell map — uses side+range lookup). */
  private blockadeWalls: Map<number, BlockadeWall> = new Map();
  private nextBlockadeWallId = 1;

  /** Cached total cube count; -1 means "dirty, recompute on next access". */
  private cachedTotalCubes = -1;

  constructor(config: BoardConfig) {
    this.grid = config.grid.map(row => row.map(stack => [...stack]));
    if (config.snakeCells) {
      this.snakeCells = new Set(config.snakeCells);
    }
    if (config.megaBlockDefs) {
      for (const def of config.megaBlockDefs) {
        this.addMegaBlock(def);
      }
    }
    if (config.lockedDoorDefs) {
      for (const def of config.lockedDoorDefs) {
        this.addLockedDoor(def);
      }
    }
    if (config.blockadeWallDefs) {
      for (const def of config.blockadeWallDefs) {
        const colorIndex = def.colorIndex ?? 0;
        this.addBlockadeWall(def, colorIndex);
      }
    }
    if (config.barricadeDefs) {
      for (const def of config.barricadeDefs) {
        this.addBarricade(def);
      }
    }
  }

  private addMegaBlock(def: MegaBlockDef): MegaBlock {
    const id = this.megaBlocks.allocateId();
    const mb: MegaBlock = {
      id,
      colorIndex: def.colorIndex,
      hp: def.hp,
      maxHp: def.hp,
      row: def.row,
      col: def.col,
      width: def.width,
      height: def.height,
    };
    const cells: number[] = [];
    for (let r = def.row; r < def.row + def.height; r++) {
      for (let c = def.col; c < def.col + def.width; c++) {
        if (r < this.grid.length && c < this.grid[0].length) {
          cells.push(cellKey(r, c));
          this.grid[r][c] = [def.colorIndex];
        }
      }
    }
    this.megaBlocks.add(mb, cells);
    return mb;
  }

  get rows(): number {
    return this.grid.length;
  }

  get cols(): number {
    return this.grid.length > 0 ? this.grid[0].length : 0;
  }

  getTopColor(row: number, col: number): number | null {
    const stack = this.grid[row][col];
    return stack.length > 0 ? stack[stack.length - 1] : null;
  }

  getTopGameplayColor(row: number, col: number): number | null {
    // The grid already contains gameplay color indices (0-5), not visual palette indices.
    // No gameplayMap translation needed — that mapping is only for rendering (visual palette → gameplay).
    return this.getTopColor(row, col);
  }

  removeTopCube(row: number, col: number): number | null {
    const stack = this.grid[row][col];
    if (stack.length === 0) return null;
    this.cachedTotalCubes = -1;
    return stack.pop()!;
  }

  totalCubesRemaining(): number {
    if (this.cachedTotalCubes >= 0) return this.cachedTotalCubes;
    let count = 0;
    for (let r = 0; r < this.grid.length; r++) {
      for (let c = 0; c < this.grid[r].length; c++) {
        const key = cellKey(r, c);
        if (this.megaBlocks.cellMap.has(key)) {
          continue;
        }
        count += this.grid[r][c].length;
      }
    }
    for (const mb of this.megaBlocks.entities.values()) {
      count += mb.hp;
    }
    this.cachedTotalCubes = count;
    return count;
  }

  isCleared(): boolean {
    return this.totalCubesRemaining() === 0;
  }

  findTopCubesByColor(colorIndex: number): GridPosition[] {
    const positions: GridPosition[] = [];
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        // Skip cells hidden behind locked doors or barricades — they can't be targeted
        if (this.isDoorCell(row, col)) continue;
        if (this.isBarricadeCell(row, col)) continue;
        if (this.getTopGameplayColor(row, col) === colorIndex) {
          positions.push({ row, col });
        }
      }
    }
    return positions;
  }

  getStack(row: number, col: number): readonly number[] {
    return this.grid[row][col];
  }

  getGrid(): readonly (readonly (readonly number[])[])[] {
    return this.grid;
  }

  // ── Mega block methods ──────────────────────────────────────────────────

  /** Check if a cell is part of a mega block. */
  isMegaBlockCell(row: number, col: number): boolean {
    return this.megaBlocks.isCell(row, col);
  }

  /** Get the mega block ID for a cell, or undefined if not a mega block cell. */
  getMegaBlockId(row: number, col: number): number | undefined {
    return this.megaBlocks.getCellId(row, col);
  }

  /** Get a mega block by ID. */
  getMegaBlock(id: number): MegaBlock | undefined {
    return this.megaBlocks.get(id);
  }

  /** Get all mega blocks. */
  getMegaBlocks(): ReadonlyMap<number, MegaBlock> {
    return this.megaBlocks.getAll();
  }

  /**
   * Hit a mega block: decrement its HP by 1.
   * Returns the mega block. If HP reaches 0, removes all cells and the mega block.
   */
  hitMegaBlock(id: number): MegaBlock | null {
    const mb = this.megaBlocks.get(id);
    if (!mb) return null;
    this.cachedTotalCubes = -1;
    mb.hp--;
    if (mb.hp <= 0) {
      for (let r = mb.row; r < mb.row + mb.height; r++) {
        for (let c = mb.col; c < mb.col + mb.width; c++) {
          if (r < this.grid.length && c < this.grid[0].length) {
            this.grid[r][c] = [];
          }
        }
      }
      this.megaBlocks.remove(id);
    }
    return mb;
  }

  /**
   * Count cubes per color, including mega block HP.
   * Each HP point of a mega block counts as one cube of that color.
   */
  countCubesByColor(): Map<number, number> {
    const counts = new Map<number, number>();
    for (let r = 0; r < this.grid.length; r++) {
      for (let c = 0; c < this.grid[r].length; c++) {
        const key = cellKey(r, c);
        if (this.megaBlocks.cellMap.has(key)) continue;
        for (const val of this.grid[r][c]) {
          counts.set(val, (counts.get(val) ?? 0) + 1);
        }
      }
    }
    for (const mb of this.megaBlocks.entities.values()) {
      counts.set(mb.colorIndex, (counts.get(mb.colorIndex) ?? 0) + mb.hp);
    }
    return counts;
  }

  /**
   * Returns true if there are any cubes of the given color anywhere on the board,
   * including behind locked doors, barricades, or inside mega blocks.
   * This is deliberately CONSERVATIVE — if the color exists anywhere, returns true.
   */
  hasTargetsForColor(colorIndex: number): boolean {
    for (let r = 0; r < this.grid.length; r++) {
      for (let c = 0; c < this.grid[r].length; c++) {
        const key = cellKey(r, c);
        if (this.megaBlocks.cellMap.has(key)) continue;
        for (const val of this.grid[r][c]) {
          if (val === colorIndex) return true;
        }
      }
    }
    for (const mb of this.megaBlocks.entities.values()) {
      if (mb.colorIndex === colorIndex && mb.hp > 0) return true;
    }
    return false;
  }

  // ── Locked door methods ────────────────────────────────────────────────

  /**
   * Add a locked door to the board.
   * If colorIndex is not set in the def, it must be provided by the caller
   * (e.g. picked from available colors at level load time).
   */
  private addLockedDoor(def: LockedDoorDef): LockedDoor {
    const id = this.lockedDoors.allocateId();
    const door: LockedDoor = {
      id,
      colorIndex: def.colorIndex ?? 0,
      hp: def.hp,
      maxHp: def.hp,
      row: def.row,
      col: def.col,
      width: def.width,
      height: def.height,
    };
    const cells: number[] = [];
    for (let r = def.row; r < def.row + def.height; r++) {
      for (let c = def.col; c < def.col + def.width; c++) {
        if (r < this.grid.length && c < this.grid[0].length) {
          cells.push(cellKey(r, c));
        }
      }
    }
    this.lockedDoors.add(door, cells);
    return door;
  }

  /**
   * Called when a cube of the given color is cleared anywhere on the board.
   * Decrements HP of all active locked doors matching this colorIndex.
   * Returns list of doors that just unlocked (HP reached 0).
   */
  hitDoor(colorIndex: number): LockedDoor[] {
    const unlocked: LockedDoor[] = [];
    for (const door of this.lockedDoors.entities.values()) {
      if (door.colorIndex === colorIndex && door.hp > 0) {
        door.hp--;
        if (door.hp <= 0) {
          this.lockedDoors.remove(door.id);
          unlocked.push(door);
        }
      }
    }
    return unlocked;
  }

  /** Check if a cell is covered by an active locked door (hidden from shooting/rendering). */
  isDoorCell(row: number, col: number): boolean {
    return this.lockedDoors.isCell(row, col);
  }

  /** Get the locked door covering a cell, or null if none. */
  getDoorAt(row: number, col: number): LockedDoor | null {
    return this.lockedDoors.getAt(row, col) ?? null;
  }

  /** Get all active locked doors (HP > 0). */
  getActiveDoors(): ReadonlyMap<number, LockedDoor> {
    return this.lockedDoors.getAll();
  }

  /** Get a locked door by ID. */
  getLockedDoor(id: number): LockedDoor | undefined {
    return this.lockedDoors.get(id);
  }

  // ── Snake block methods ──────────────────────────────────────────────────

  /** Check if a cell is part of a snake block. */
  isSnakeCell(row: number, col: number): boolean {
    return this.snakeCells.has(cellKey(row, col));
  }

  /** Get all snake cell keys. */
  getSnakeCells(): ReadonlySet<number> {
    return this.snakeCells;
  }

  /**
   * Find the bottom-most non-empty row in the same column that is part of the
   * same contiguous snake run as (startRow, col). Searches downward from startRow.
   */
  findSnakeBottomRow(startRow: number, col: number): number {
    let bottomRow = startRow;
    for (let r = startRow + 1; r < this.rows; r++) {
      if (this.isSnakeCell(r, col) && this.grid[r][col].length > 0) {
        bottomRow = r;
      } else {
        break;
      }
    }
    return bottomRow;
  }

  /**
   * Remove the bottom cell of a snake column. Returns the color removed,
   * the row it was removed from, or null if no cell to remove.
   * Also cleans up the snakeCells entry for the removed cell.
   */
  removeSnakeBottomCube(topRow: number, col: number): { color: number; row: number } | null {
    const bottomRow = this.findSnakeBottomRow(topRow, col);
    const stack = this.grid[bottomRow][col];
    if (stack.length === 0) return null;
    this.cachedTotalCubes = -1;
    const color = stack.pop()!;
    // If the cell is now empty, remove it from snake tracking
    if (stack.length === 0) {
      this.snakeCells.delete(cellKey(bottomRow, col));
    }
    return { color, row: bottomRow };
  }

  // ── Blockade wall methods ──────────────────────────────────────────────

  /**
   * Add a blockade wall to the board.
   * colorIndex must be provided by the caller (resolved at level load time).
   */
  private addBlockadeWall(def: BlockadeWallDef, colorIndex: number): BlockadeWall {
    const id = this.nextBlockadeWallId++;
    const wall: BlockadeWall = {
      id,
      side: def.side,
      colorIndex,
      hp: def.hp,
      maxHp: def.hp,
      startCell: def.startCell,
      length: def.length,
    };
    this.blockadeWalls.set(id, wall);
    return wall;
  }

  /**
   * Hit a blockade wall: decrement HP by 1.
   * Returns the wall state after the hit. If HP reaches 0, removes the wall.
   */
  hitBlockadeWall(id: number): { destroyed: boolean; remainingHp: number } | null {
    const wall = this.blockadeWalls.get(id);
    if (!wall) return null;
    wall.hp--;
    const destroyed = wall.hp <= 0;
    if (destroyed) {
      this.blockadeWalls.delete(id);
    }
    return { destroyed, remainingHp: wall.hp };
  }

  /** Get all active blockade walls (HP > 0). */
  getActiveBlockadeWalls(): ReadonlyMap<number, BlockadeWall> {
    return this.blockadeWalls;
  }

  /** Get a blockade wall by ID. */
  getBlockadeWall(id: number): BlockadeWall | undefined {
    return this.blockadeWalls.get(id);
  }

  /**
   * Check if a shot from a given side at a given cell index is blocked by a blockade wall.
   * For left/right walls, cellIndex is the row. For top/bottom walls, cellIndex is the column.
   * Returns the first blocking wall found, or null if no wall blocks.
   */
  isBlockedByWall(side: 'top' | 'bottom' | 'left' | 'right', cellIndex: number): { wallId: number; colorIndex: number } | null {
    for (const wall of this.blockadeWalls.values()) {
      if (wall.side !== side) continue;
      if (cellIndex >= wall.startCell && cellIndex < wall.startCell + wall.length) {
        return { wallId: wall.id, colorIndex: wall.colorIndex };
      }
    }
    return null;
  }

  // ── Barricade methods ──────────────────────────────────────────────────

  /**
   * Add a barricade to the board. Barricades cover a rectangular region of cells,
   * blocking shots until broken by hammers (not by clearing cubes).
   */
  private addBarricade(def: BarricadeDef): Barricade {
    const id = this.barricades.allocateId();
    const barricade: Barricade = {
      id,
      hp: def.hp,
      maxHp: def.hp,
      row: def.row,
      col: def.col,
      width: def.width,
      height: def.height,
    };
    const cells: number[] = [];
    for (let r = def.row; r < def.row + def.height; r++) {
      for (let c = def.col; c < def.col + def.width; c++) {
        if (r < this.grid.length && c < this.grid[0].length) {
          cells.push(cellKey(r, c));
        }
      }
    }
    this.barricades.add(barricade, cells);
    return barricade;
  }

  /**
   * Hit a barricade: decrement HP by 1 (called when a hammer strikes it).
   * Returns the barricade state after the hit. If HP reaches 0, removes the barricade
   * and clears all its cell mappings (revealing the cubes underneath).
   */
  hitBarricade(id: number): { destroyed: boolean; hp: number } {
    const barricade = this.barricades.get(id);
    if (!barricade) return { destroyed: false, hp: 0 };
    barricade.hp--;
    const destroyed = barricade.hp <= 0;
    if (destroyed) {
      this.barricades.remove(id);
    }
    return { destroyed, hp: barricade.hp };
  }

  /** Check if a cell is covered by an active barricade (hidden from shooting). */
  isBarricadeCell(row: number, col: number): boolean {
    return this.barricades.isCell(row, col);
  }

  /** Get the barricade covering a cell, or undefined if none. */
  getBarricadeAt(row: number, col: number): Barricade | undefined {
    return this.barricades.getAt(row, col);
  }

  /** Get all active barricades. */
  getActiveBarricades(): Barricade[] {
    return Array.from(this.barricades.entities.values());
  }

  /** Get a barricade by ID. */
  getBarricade(id: number): Barricade | undefined {
    return this.barricades.get(id);
  }
}
